import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { useEffect, useState } from 'react';
import LightTimeChangeDialog from '../components/LightTimeChangeDialog';
import {
  invalidLightProfiles,
  lightCycle,
  supportsLightChannels,
  supportsScheduledLightChannels,
  scheduleLightWarnings
} from '../Utils/light_profiles';

import Typography from '@material-ui/core/Typography';

import Button from '@material-ui/core/Button';

import { MyTimePicker } from '../FormWidgets/DatePicker.js';
import Desc from '../FormWidgets/Description';
import { SettingGroup, ValueBox } from '../FormWidgets/SettingGroup';
import { EventGapSelect, Select } from '../FormWidgets/Select.js';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { SettingActions } from '../components/index.js';

import _ from 'lodash';
import { addZ } from '../Utils/utils.js';

import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useTimingSettings } from '../Hooks/useSystemClockSettings';
import ControllerClock from '../FormWidgets/ControllerClock';
import { supportsSystemClockRelocation, withoutControllerClock } from '../Utils/system_settings_utils';
import { subscribeKey } from 'valtio/utils';
import { useSnapshot, subscribe } from 'valtio';
import { useTranslation } from 'react-i18next';
import { ScheduleMarker, ScheduleDraftStatus, useScheduledSettings } from './Scheduling/ScheduledSettings';
import { StaticEventTabs, StaticIntervalTabs } from './StaticTimingTabs.js';
import IndexedSettingsTabs from '../components/IndexedSettingsTabs.js';
import { settingsLeafPaths } from '../Utils/settings_draft';

function makeTimeIndustrialPro(data, seconds = false) {
  let str = `${addZ(data.hour)}:${addZ(data.minute)}`;
  if (seconds) {
    str += `:${addZ(data.second)}`;
  }
  return str;
}

function makeJsonTime(seconds = true) {
  let t = {
    hour: 0,
    minute: 0
  };
  if (seconds) {
    t.second = 0;
  }

  return t;
}

function handleDateTimeInputIndustrialPro(name, value, controls, seconds = false) {
  let d = [
    [`${name}.hour`, `${name}.minute`],
    [value.getHours(), value.getMinutes()]
  ];

  if (seconds) {
    d[0].push(`${name}.second`);
    d[1].push(value.getSeconds());
  }
  controls.setValue(d[0], d[1]);
}

// @param key: 'event_timer' or 'interval_timer'
function removeTimer(key, id, values, controls) {
  let timer = [...values[key]].map((t) => (t.id == id ? { ...t, __deleted__: true } : t));

  controls.setValue(key, timer);
}

function handleIntInput(name, value, values, setValues, controls) {
  let _values = { ...values };
  let v = Number(value);
  _.set(_values, name, v);

  controls.saveTempSettings(name, v);

  setValues(_values);
}

export default function TimingSettings({ ...other }) {
  const {
    t,
    params,
    stateManager,
    controller,
    parent,
    controllerSnapshot,
    parentSnapshot,
    values: ordinaryValues,
    setValues: setOrdinaryValues,
    controls: ordinaryControls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog
  } = useTimingSettings();
  const systemClock = supportsSystemClockRelocation(controllerSnapshot);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  //const infos = useSnapshot (parent.infos);

  const schedule = useScheduledSettings(controller, parent, 'timing', ordinaryValues, ordinaryControls);
  const { values, controls } = schedule;
  const setValues = schedule.setValues;
  const write_access = controllerSnapshot.access_rights.timing.write;
  // A dynamic timer list is edited as a collection; retaining only indexed
  // leaves would attach the old time to a different timer after a remote reorder.
  ['event_timer', 'interval_timer'].forEach((path) => {
    if (Array.isArray(ordinaryValues[path])) ordinaryControls.retainSetting?.(path);
  });
  const [dateInputValid, setDateInputValid] = useState(true);
  const [dateResetKey, setDateResetKey] = useState(0);
  const reset = () => {
    controls.resetSettings();
    setDateResetKey((key) => key + 1);
  };
  const transform_sent_settings = (settings) => {
    if (systemClock) return withoutControllerClock(settings);
    if (!('_date_changed' in settings) || !settings['_date_changed']) {
      settings['time_cont_year'] = 0xffff;
      settings['time_cont_month'] = 0xff;
      settings['time_cont_day'] = 0xff;
    }
    if (!('_time_changed' in settings) || !settings['_time_changed']) {
      settings['time_cont_hour'] = 0xffff;
      settings['time_cont_min'] = 0xff;
      settings['time_cont_sec'] = 0xff;
    }
    return settings;
  };

  const handleDateTimeInput = (name, format, value) => {
    if (schedule.disabled(`${name}_hour`)) return;
    const entries = format === 'date'
      ? [['year', value.getFullYear()], ['month', value.getMonth() + 1], ['day', value.getDate()]]
      : [['hour', value.getHours()], [name.includes('time_interval_2') ? 'minute' : 'min', value.getMinutes()]];
    if (format === 'time_sec') entries.push(['sec', value.getSeconds()]);
    const paths = entries.map(([field]) => `${name}_${field}`);
    const nextValues = entries.map(([, next]) => Number(next));
    if (name === 'time_cont') {
      paths.push(format === 'date' ? '_date_changed' : '_time_changed');
      nextValues.push(true);
    }
    controls.setValue(paths, nextValues);
  };

  /*
   * pack single values for hour, min, sec into one time string
   */
  function makeTime(prefix, seconds = false) {
    ['hour', prefix.includes('time_interval_2') ? 'minute' : 'min', ...(seconds ? ['sec'] : [])]
      .forEach((field) => controls.retainSetting?.(`${prefix}_${field}`));
    let str = `${addZ(values[`${prefix}_hour`])}:${addZ(values[`${prefix}_min`])}`;

    // TODO: remove this block when we switched to a more uniform naming of the timer keys
    if (prefix.includes('time_interval_2')) {
      str = `${addZ(values[`${prefix}_hour`])}:${addZ(values[`${prefix}_minute`])}`;
    }

    if (seconds) {
      str += `:${addZ(values[`${prefix}_sec`])}`;
    }

    return str;
  }
  const timeChanged = (prefix, seconds = false) =>
    ['hour', prefix.includes('time_interval_2') ? 'minute' : 'min', ...(seconds ? ['sec'] : [])]
      .some((field) => controls.hasChanged(`${prefix}_${field}`));

  /*
   * Methods for manipulating dynamic event/interval timers (industrial only)
   */

  let static_event_timers = [5055, 5155].includes(controllerSnapshot.type)
    ? [1, 2, 3, 4, 5, 6, 7, 8]
    : [1, 2, 3, 4];
  let static_interval_timers = [5055, 5155].includes(controllerSnapshot.type) ? [1, 2] : [1];
  static_event_timers.forEach((id) => controls.retainSetting?.(`time_event${id}_exec_gap`));
  const staticIntervalPrefix = (id) => (id === 1 ? 'interval' : `time_interval_${id}`);

  const addEventTimer = function () {
    let timer = [...values.event_timer];
    timer.push({
      id: getNextFreeId(values.event_timer),
      cycles: [
        {
          start: makeJsonTime(false),
          duration: makeJsonTime()
        }
      ],
      exec_gap: 0
    });

    controls.setValue(`event_timer`, timer);
  };

  // pass array of event- or interval-timer
  const getNextFreeId = (timer) => {
    for (let i = 0; ; i++) {
      if (timer.filter((t) => t.id == i).length == 0) {
        return i;
      }
    }
  };

  const addIntervalTimer = () => {
    let timer = [...values.interval_timer];
    timer.push({
      id: getNextFreeId(values.interval_timer),
      type: 0,
      on: makeJsonTime(),
      off: makeJsonTime()
    });
    controls.setValue(`interval_timer`, timer);
  };

  const [affectedLights, setAffectedLights] = useState([]);
  const commitTime = () => {
    if (!schedule.canSave || !dateInputValid) return;
    if (!controls.saveSettings(transform_sent_settings)) return;
    if (controller.type === 6000 && write_access) {
      let _values = _.cloneDeep(ordinaryValues);
      _values.interval_timer = _values.interval_timer.filter((t) => t.__deleted__ !== true);
      _values.event_timer = _values.event_timer.filter((t) => t.__deleted__ !== true);
      parent.settings.timing = _values;
      setOrdinaryValues(_values);
    }
  };

  const save = () => {
    if (!schedule.canSave || !dateInputValid) return;
    const affected = supportsLightChannels(controller)
      ? [
          ...invalidLightProfiles(
            controller.settings.light_channels,
            Number(parent.zone_id),
            lightCycle(values)
          ),
          ...(supportsScheduledLightChannels(
            controller, parent.settings.scheduling?.schedule_meta?.protocol_version
          )
            ? scheduleLightWarnings(
                controller.settings.light_channels,
                Number(parent.zone_id),
                parent.settings.scheduling,
                values
              )
            : [])
        ]
      : [];
    if (affected.length) setAffectedLights(affected);
    else commitTime();
  };

  useEffect(() => {
    if (systemClock) return;
    // Get current data and time from info values
    const time = parent.infos['time'].split(':');
    const date = parent.infos['date'].split('.');
    // Update tab-local data and time data
    let _values = { ...ordinaryValues };
    _values.controller_time = parent.infos['time'];
    _values.time_cont_hour = parseInt(time[0]);
    _values.time_cont_min = parseInt(time[1]);
    _values.time_cont_sec = parseInt(time[2]);
    _values.time_cont_year = parseInt(date[2]);
    _values.time_cont_month = parseInt(date[1]);
    _values.time_cont_day = parseInt(date[0]);
    setOrdinaryValues(_values);
    // Update controllers' data and time settings
    parent.settings.timing.controller_time = time;
    parent.settings.timing.time_cont_hour = parseInt(time[0]);
    parent.settings.timing.time_cont_min = parseInt(time[1]);
    parent.settings.timing.time_cont_sec = parseInt(time[2]);
    parent.settings.timing.controller_date = date[2] + '-' + date[1] + '-' + date[0];
    parent.settings.timing.time_cont_year = parseInt(date[2]);
    parent.settings.timing.time_cont_month = parseInt(date[1]);
    parent.settings.timing.time_cont_day = parseInt(date[0]);
  }, []);

  return (
    <>
      <LightTimeChangeDialog affected={affectedLights}
        onCancel={() => setAffectedLights([])}
        onContinue={() => { setAffectedLights([]); commitTime(); }} />
      <ScheduleDraftStatus schedule={schedule} />
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={ordinaryControls}
      />

      {schedule.canSave && <SettingActions
        title={connectionDisabledReason}
        onReset={reset}
        onSave={save}
        on_default={schedule?.saving || connectionStatus !== 'good' || !dateInputValid}
      />}

      {!systemClock && <ControllerClock controller={controllerSnapshot} liveController={controller} values={values}
        disabled={!write_access} resetKey={dateResetKey} onValidityChange={setDateInputValid}
        onDateChange={(date) => handleDateTimeInput('time_cont', 'date', date)}
        onTimeChange={(time) => handleDateTimeInput('time_cont', 'time_sec', time)} />}

      <SettingGroup controller={controllerSnapshot} id={t('start_end_time')}>
        <ValueBox controller={controllerSnapshot} id={'timing_start_day'}>
          <Box display="flex" alignItems="center" gridGap={8} mb={1}>
            <ScheduleMarker target={schedule('start_day_hour')} />
            <Typography><Desc id="start_day" /></Typography>
          </Box>
          <MyTimePicker
            value={makeTime('start_day')}
            unsaved={timeChanged('start_day')}
            seconds={false}
            name="start_day"
            disabled={schedule.disabled('start_day_hour')}
            onChange={(value) => handleDateTimeInput('start_day', 'time_min', value)}
          />
        </ValueBox>
        <ValueBox controller={controllerSnapshot} id={'timing_end_day'}>
          <Box display="flex" alignItems="center" gridGap={8} mb={1}>
            <ScheduleMarker target={schedule('end_day_hour')} />
            <Typography><Desc id="end_day" /></Typography>
          </Box>
          <MyTimePicker
            value={makeTime('end_day')}
            unsaved={timeChanged('end_day')}
            seconds={false}
            name="end_day"
            disabled={schedule.disabled('end_day_hour')}
            onChange={(value) => handleDateTimeInput('end_day', 'time_min', value)}
          />
        </ValueBox>
      </SettingGroup>

      {/* ------------ dynamic interval timer (industrial) --------------*/}
      {controllerSnapshot.type >= 6000 ? (
        <>
          <IndexedSettingsTabs
            items={values.interval_timer.map((timer, index) => ({
              id: timer.id,
              label: `${t('io_interval')} ${index + 1}`
            }))}
            idPrefix="timing-dynamic-interval"
            contained={true}
            renderPanel={(_, index) => (
              <IntervalTimerIndustrialPro
                index={index}
                id={values.interval_timer[index].id}
                values={values}
                setValues={setValues}
                controls={controls}
                controller={controllerSnapshot}
                write_access={write_access}
              />
            )}
          />
          <Button startIcon={<AddIcon />} color="primary" onClick={() => addIntervalTimer()}>
            {t('add_interval_timer')}
          </Button>
        </>
      ) : (
        <StaticIntervalTabs
          ids={static_interval_timers}
          t={t}
          idPrefix="timing-interval"
          disabled={(id) => schedule.disabled(`${staticIntervalPrefix(id)}_on_day_hour`)}
          renderInfo={(id) => <ScheduleMarker target={schedule(`${staticIntervalPrefix(id)}_on_day_hour`)} />}
          controller={controllerSnapshot}
          getValue={(id, field) => makeTime(`${staticIntervalPrefix(id)}_${field}`, true)}
          getUnsaved={(id, field) => timeChanged(`${staticIntervalPrefix(id)}_${field}`, true)}
          onChange={(id, field, value) =>
            handleDateTimeInput(`${staticIntervalPrefix(id)}_${field}`, 'time_sec', value)
          }
        />
      )}

      {controllerSnapshot.type >= 6000 ? (
        <>
          {/* ------------ dynamic event timer (industrial) --------------*/}
          <IndexedSettingsTabs
            items={values.event_timer.map((timer, index) => ({
              id: timer.id,
              label: `${t('io_event')} ${index + 1}`
            }))}
            idPrefix="timing-dynamic-event"
            contained={true}
            renderPanel={(_, index) => {
              const timer = values.event_timer[index];
              return (
                <EventTimerIndustrialPro
                  index={index}
                  id={timer.id}
                  controllerTimer={parentSnapshot.settings.timing.event_timer.find(
                    (savedTimer) => savedTimer.id === timer.id
                  )}
                  values={values}
                  setValues={setValues}
                  controls={controls}
                />
              );
            }}
          />

          <Button startIcon={<AddIcon />} color="primary" onClick={() => addEventTimer()}>
            {t('add_event_timer')}
          </Button>
        </>
      ) : (
        <StaticEventTabs
          ids={static_event_timers}
          t={t}
          idPrefix="timing-event"
          disabled={(id) => schedule.disabled(`event${id}_start_hour`)}
          renderInfo={(id) => <ScheduleMarker target={schedule(`event${id}_start_hour`)} />}
          controller={controllerSnapshot}
          getValue={(id, field) =>
            field === 'exec_gap'
              ? values[`time_event${id}_exec_gap`]
              : makeTime(`event${id}_${field}`, field === 'duration')
          }
          getControllerValue={(id, field) =>
            field === 'exec_gap' ? parentSnapshot.settings.timing[`time_event${id}_exec_gap`] : undefined
          }
          getUnsaved={(id, field) => timeChanged(`event${id}_${field}`, field === 'duration')}
          onTimeChange={(id, field, value) =>
            handleDateTimeInput(
              `event${id}_${field}`,
              field === 'duration' ? 'time_sec' : 'time_min',
              value
            )
          }
          onGapChange={(id, value) =>
            handleIntInput(`time_event${id}_exec_gap`, value, values, setValues, controls)
          }
        />
      )}

      {schedule.canSave && <SettingActions
        title={connectionDisabledReason}
        onReset={reset}
        onSave={save}
        on_default={schedule?.saving || connectionStatus !== 'good' || !dateInputValid}
      />}
    </>
  );
}

export const default_event_timer = { id: 0, cycles: [], exec_gap: 0 };
export const default_interval_timer = {
  id: 0,
  on: { hour: 0, minute: 0, second: 0 },
  off: { hour: 0, minute: 0, second: 0 },
  type: 0
};

export function EventTimerIndustrialPro({
  index,
  id,
  controllerTimer,
  values,
  setValues,
  controls,
  value_path_prefix = '',
  allowDelete = true
}) {
  let { t } = useTranslation();
  let t_path = `${value_path_prefix}event_timer[${index}]`;
  let timer = _.get(values, t_path, {});
  timer = { ...default_event_timer, ...timer };
  settingsLeafPaths(timer, t_path).forEach((path) => controls.retainSetting?.(path));

  const addCycle = function (et) {
    let cycles = [..._.get(values, `${t_path}.cycles`, [])];
    cycles.push({
      start: makeJsonTime(false),
      duration: makeJsonTime()
    });
    controls.setValue(`${t_path}.cycles`, cycles);
  };

  const removeCycle = function (et) {
    let cycles = [..._.get(values, `${t_path}.cycles`, [])];
    cycles.pop();
    controls.setValue(`${t_path}.cycles`, cycles);
  };

  return (
    <SettingGroup label={`${t('event')} ${id + 1}`}>
      {timer.__deleted__ !== true ? (
        <>
          {_.get(values, `${t_path}.cycles`, []).map((cycle, c) => (
            <Box>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${index}_start${c}`} show="event_start" />
                </Typography>
                <MyTimePicker
                  value={makeTimeIndustrialPro(cycle.start)}
                  unsaved={controls.hasChanged?.(`${t_path}.cycles[${c}].start`)}
                  seconds={false}
                  name={`event${index}_start${c}`}
                  onChange={(value) =>
                    handleDateTimeInputIndustrialPro(
                      `${t_path}.cycles[${c}].start`,
                      value,
                      controls
                    )
                  }
                />
              </ValueBox>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${index}_duration${c}`} show="event_duration" />
                </Typography>
                <MyTimePicker
                  value={makeTimeIndustrialPro(cycle.duration, true)}
                  unsaved={controls.hasChanged?.(`${t_path}.cycles[${c}].duration`)}
                  seconds={true}
                  name={`event_timer_${index}_${c}_duration`}
                  onChange={(value) =>
                    handleDateTimeInputIndustrialPro(
                      `${t_path}.cycles[${c}].duration`,
                      value,
                      controls,
                      true
                    )
                  }
                />
              </ValueBox>
            </Box>
          ))}

          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Button startIcon={<AddIcon />} onClick={() => addCycle(index)}>
              {t('add_cycle')}
            </Button>
            {(timer.cycles ?? []).length > 1 && (
              <Button startIcon={<DeleteIcon />} onClick={() => removeCycle(index)}>
                {t('remove_cycle')}
              </Button>
            )}
          </Box>
          <ValueBox style={{ maxWidth: 'fit-content' }}>
            <Typography gutterBottom>
              <Desc id={`time_event${index}_exec_gap`} show="event_exec_gap" />
            </Typography>
            <EventGapSelect
              value={timer.exec_gap ?? 0}
              controller_value={controllerTimer?.exec_gap}
              onChange={(event, value) =>
                handleIntInput(
                  `${t_path}.exec_gap`,
                  event.target.value,
                  values,
                  setValues,
                  controls
                )
              }
            />
          </ValueBox>
          {allowDelete && (
            <Button
              startIcon={<DeleteIcon />}
              color="secondary"
              onClick={() => removeTimer('event_timer', timer.id, values, controls)}
            >
              {t('remove_event_timer')}
            </Button>
          )}
        </>
      ) : (
        <>
          {t('deleted')}
          <br />
          {t('click save to confirm')}
        </>
      )}
    </SettingGroup>
  );
}

export function IntervalTimerIndustrialPro({
  index,
  id,
  values,
  setValues,
  controls,
  controller,
  write_access,
  value_path_prefix = '',
  allowDelete = true
}) {
  let { t } = useTranslation();

  let t_path = `${value_path_prefix}interval_timer[${index}]`;
  let timer = _.get(values, t_path, {});
  timer = { ...default_interval_timer, ...timer };
  settingsLeafPaths(timer, t_path).forEach((path) => controls.retainSetting?.(path));
  return (
    <SettingGroup label={`${t('interval')} ${id + 1}`}>
      {timer.__deleted__ !== true ? (
        <>
          <ValueBox controller={controller} id={'timing_interval_on'}>
            <Typography gutterBottom>
              <Desc id="interval_on" name="Interval On" />
            </Typography>
            <MyTimePicker
              value={makeTimeIndustrialPro(timer.on, true)}
              unsaved={controls.hasChanged?.(`${t_path}.on`)}
              seconds={true}
              name="interval_on"
              onChange={(value) =>
                handleDateTimeInputIndustrialPro(`${t_path}.on`, value, controls, true)
              }
            />
          </ValueBox>
          <ValueBox>
            <Typography gutterBottom>
              <Desc id="interval_off" name="Interval Off" />
            </Typography>
            <MyTimePicker
              value={makeTimeIndustrialPro(timer.off, true)}
              unsaved={controls.hasChanged?.(`${t_path}.off`)}
              seconds={true}
              name="interval_off"
              onChange={(value) =>
                handleDateTimeInputIndustrialPro(`${t_path}.off`, value, controls, true)
              }
            />
          </ValueBox>
          <ValueBox>
            <Typography gutterBottom>
              <Desc id="interval_phase" name="Phase" />
            </Typography>
            <Select
              value={timer.type}
              unsaved={controls.hasChanged?.(`${t_path}.type`)}
              onChange={(evt) => controls.setValue(`${t_path}.type`, evt.target.value)}
            >
              <MenuItem value={0} selected={true}>
                {t('Day')}
              </MenuItem>
              <MenuItem value={1}>{t('Night')}</MenuItem>
              <MenuItem value={2}>{t('Day + Night')}</MenuItem>
            </Select>
          </ValueBox>

          {allowDelete && (
            <Button
              startIcon={<DeleteIcon />}
              color="secondary"
              onClick={() => removeTimer('interval_timer', id, values, controls)}
            >
              {t('remove_interval_timer')}
            </Button>
          )}
        </>
      ) : (
        <>
          {t('deleted')}
          <br />
          {t('click save to confirm')}
        </>
      )}
    </SettingGroup>
  );
}
